import React, { createContext, useState, useCallback } from 'react';

export const AppStatusContext = createContext();

export const AppStatusProvider = ({ children }) => {
  const [status, setStatus] = useState({
    isDown: false,
    errorType: null,  // 'server', 'network', 'database', 'unknown'
    message: null,
    lastAttempt: null,
    attemptCount: 0
  });

  const getDefaultMessage = (errorType) => {
    const messages = {
      'server': 'Our servers are temporarily unavailable. We\'re working to restore service.',
      'network': 'Network connection issue. Please check your internet and try again.',
      'database': 'Database is temporarily offline. Our team is restoring it now.',
      'unknown': 'We\'re experiencing technical difficulties. Thank you for your patience.'
    };
    return messages[errorType] || messages.unknown;
  };

  const markServerDown = useCallback((errorType = 'unknown', message = null) => {
    setStatus(prev => ({
      ...prev,
      isDown: true,
      errorType,
      message: message || getDefaultMessage(errorType),
      lastAttempt: new Date(),
      attemptCount: prev.attemptCount + 1
    }));
  }, []);

  const markServerUp = useCallback(() => {
    setStatus({
      isDown: false,
      errorType: null,
      message: null,
      lastAttempt: null,
      attemptCount: 0
    });
  }, []);

  const resetStatus = useCallback(() => {
    setStatus({
      isDown: false,
      errorType: null,
      message: null,
      lastAttempt: null,
      attemptCount: 0
    });
  }, []);

  return (
    <AppStatusContext.Provider value={{ status, markServerDown, markServerUp, resetStatus }}>
      {children}
    </AppStatusContext.Provider>
  );
};

export const useAppStatus = () => {
  const context = React.useContext(AppStatusContext);
  if (!context) {
    throw new Error('useAppStatus must be used within AppStatusProvider');
  }
  return context;
};
